import React, { useEffect } from 'react';
import {
  Box,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Heading,
  Text,
  useColorModeValue,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { FiUsers, FiServer, FiMonitor, FiMapPin, FiCreditCard } from 'react-icons/fi';
import { useAuthStore } from '@/stores/authStore';
import { useDashboardStore } from '@/stores/dashboardStore';
import { AnalyticsReports, TenantOverview } from '@/components/dashboard';
import { useTranslation } from 'react-i18next';

interface StatCardProps {
  title: string;
  stat: string;
  icon: React.ElementType;
  helpText?: string;
}

const StatCard = ({ title, stat, icon, helpText }: StatCardProps) => {
  const bg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  return (
    <Stat
      px={{ base: 2, md: 4 }}
      py="5"
      shadow="sm"
      border="1px"
      borderColor={borderColor}
      rounded="lg"
      bg={bg}
    >
      <Flex justifyContent="space-between">
        <Box pl={{ base: 2, md: 4 }}>
          <StatLabel fontWeight="medium">{title}</StatLabel>
          <StatNumber fontSize="2xl" fontWeight="bold">
            {stat}
          </StatNumber>
          {helpText && <StatHelpText>{helpText}</StatHelpText>}
        </Box>
        <Box
          my="auto"
          color={useColorModeValue('gray.800', 'gray.200')}
          alignContent="center"
        >
          <Icon as={icon} w={8} h={8} />
        </Box>
      </Flex>
    </Stat>
  );
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { stats, isLoadingStats, statsError, fetchStats } = useDashboardStore();
  
  useEffect(() => {
    // Only fetch if user is logged in
    if (user) {
      fetchStats(
        user.role === 'SYSTEM',
        user.tenantId
      );
    }
  }, [user, fetchStats]);
  
  const isSystem = user?.role === 'SYSTEM';
  
  // For tenant admins, show the rich overview component
  if (!isSystem || isSystem) {
    return <AnalyticsReports />;
  }
  
  // For system users, show the simple stats view
  return (
    <Box>
      <Heading size="lg" mb={6}>
        {t('dashboard.systemDashboard')}
      </Heading>
      
      {isLoadingStats ? (
        <Text>{t('common.loading')}</Text>
      ) : statsError ? (
        <Text color="red.500">{t('common.error')}: {statsError}</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="6">
          <StatCard
            title={t('dashboard.stats.totalUsers')}
            stat={stats?.users?.toString() || '0'}
            icon={FiUsers}
          />
          
          <StatCard
            title={t('dashboard.stats.totalTenants')}
            stat={stats?.tenants?.toString() || '0'}
            icon={FiServer}
          />
          
          <StatCard
            title={t('dashboard.stats.totalDevices')}
            stat={stats?.devices?.toString() || '0'}
            icon={FiMonitor}
          />
          
          <StatCard
            title={t('dashboard.stats.totalParkings')}
            stat={stats?.parkings?.toString() || '0'}
            icon={FiMapPin}
            helpText={t('dashboard.stats.allTime')}
          />
          
          <StatCard
            title={t('dashboard.stats.totalPayments')}
            stat={stats?.payments?.toString() || '0'}
            icon={FiCreditCard}
            helpText={t('dashboard.stats.allTime')}
          />
        </SimpleGrid>
      )}
    </Box>
  );
};

export default Dashboard; 